import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronRightIcon } from '@page/icons';

import { cn } from '../../lib/styles';

function Accordion({
  className,
  naked = false,
  ...rest
}: React.ComponentProps<typeof AccordionPrimitive.Root> & { naked?: boolean }) {
  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      className={cn(
        'border-b-0 overflow-hidden',
        {
          'border border-border-default rounded-sm bg-gray-2': !naked,
        },
        className,
      )}
      {...rest}
    />
  );
}

function AccordionItem({ className, ...props }: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return <AccordionPrimitive.Item data-slot="accordion-item" className={className} {...props} />;
}

function AccordionTrigger({
  className,
  children,
  containerClassName,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & { containerClassName?: string }) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'cursor-pointer h-14 flex flex-1 gap-4 transition-all outline-none',
          'focus-visible:ring-[1px] focus-visible:ring-blue-300 disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]_svg[data-slot=accordion-trigger-icon]]:rotate-90',
          'disabled:[&_[data-slot=accordion-trigger-icon]]:opacity-0',
          'bg-gray-2 hover:bg-gray-5',
          containerClassName,
        )}
        {...props}
      >
        <div
          className={cn(
            'pl-4 pr-2 flex flex-1 items-center justify-between gap-2',
            'text-left font-body-md capitalize font-semibold text-text-primary',
            className,
          )}
        >
          {children}

          <div className="flex items-center justify-center size-6">
            <ChevronRightIcon
              data-slot="accordion-trigger-icon"
              className="icon-primary pointer-events-none size-5 transition-transform duration-200"
            />
          </div>
        </div>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  containerClassName,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content> & { containerClassName?: string }) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={cn(
        'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden',
        'border border-border-default rounded-t-xl border-b-0 border-l-0 border-r-0',
        'bg-bg-sur-primary',
        containerClassName,
      )}
      {...props}
    >
      <div className={cn('pb-4 px-2', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
